import React from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import styles from './ScopeChangesEditInventoryItemsDialog.sass'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import cx from 'classnames'
import ScopeChangeEditInventoryItemsDialogViewModel, {
  EditScopeChangeInventoryItemViewModel,
} from './ScopeChangeEditInventoryItemsDialogViewModel'
import Tooltip from 'components/Tooltip'
import AddInventoryItemsToEditDialog from '../AddInventoryItemsToEditDialog'

interface Props {
  vm: ScopeChangeEditInventoryItemsDialogViewModel
}

export default observer(function ScopeChangeEditInventoryItemsDialog({
  vm,
}: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle>Edit service items</ModalSheetTitle>
      </ModalSheetHeader>
      <InventoryItemHeadings />
      <ModalSheetContent>
        <div className={styles.itemsContainer}>
          {vm.editScopeChangeInventoryItems.map((item) => {
            return (
              <EditScopeChangeInventoryItem
                key={item.key}
                item={item}
                remove={() => vm.removeScopeChange(item.versionId)}
              />
            )
          })}
          <div className={styles.addItemContainer}>
            <div className={styles.itemText}>Need more service items?</div>
            <Button
              type="text"
              dataTest="edit-inventory-scope-changes-add-button"
              onClick={() =>
                vm.addInventoryItemsToEditDialogVM.open(vm.scopeChangeRequest)
              }
            >
              Add more
            </Button>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} type="default">
            Close
          </Button>
          <Button
            onClick={vm.save}
            type="primary"
            dataTest="scope-change-request-edit"
            loading={vm.save.pending}
            disabled={vm.save.pending}
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      {vm.addInventoryItemsToEditDialogVM.showing && (
        <AddInventoryItemsToEditDialog
          vm={vm.addInventoryItemsToEditDialogVM}
        />
      )}
    </ModalSheet>
  )
})

const InventoryItemHeadings = observer(function () {
  return (
    <div className={styles.subheading}>
      <span>Service items</span>

      <div className={styles.subheadingCurrentContainer}>
        <span className={styles.subheadingCurrent}>Current</span>
        <Tooltip
          content={'Current quantity for each item on the job.'}
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </div>
      <div className={styles.subheadingNewTotalContainer}>
        <span className={styles.subheadingNewTotal}>New total</span>
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </div>
    </div>
  )
})

const EditScopeChangeInventoryItem = observer(function ({
  item,
  remove,
}: {
  item: EditScopeChangeInventoryItemViewModel
  remove: (key: string) => void
}) {
  return (
    <div className={cx(styles.itemContainer)}>
      <div className={styles.textContainer}>
        <div className={styles.title}>{item.name}</div>
      </div>

      <div className={styles.currentQuantityContainer}>
        {item.currentQuantity}
      </div>

      <div
        className={cx(
          styles.itemQuantityContainer,
          item.validation.getError('newQuantity') && styles.hasError
        )}
      >
        <IconButton>
          <Icon name="subtract" size={16} onClick={item.decrementQuantity} />
        </IconButton>

        <input
          key="quantity"
          type="number"
          min="0"
          className={cx(styles.input, styles.itemQuantity)}
          value={item.newQuantity}
          onChange={(e) => item.setQuantity(e.target.value)}
          data-test={`scope-change-inventory-quantity-${item.versionId}`}
        />

        <IconButton onClick={item.incrementQuantity}>
          <Icon name="add" size={16} />
        </IconButton>
      </div>

      <IconButton
        className={styles.removeItemButton}
        onClick={() => remove(item.key)}
        dataTest={`request-scope-changes-inventory-remove-button-${item.versionId}`}
      >
        <Icon name="close" size={24} />
      </IconButton>
    </div>
  )
})
